import { InfoIcon } from 'lucide-react';
import React, { ReactNode } from 'react';

import { Loader } from '@/components/Loader';
import { Tooltip } from '@/components/Tooltip';
import { Container } from '@/components/ui';
import {
  MONITORING_NAME_MAP,
  MONITORING_TOOLTIP_MAP,
} from '@/hooks/monitoringConstants';
import {
  MonitoringCategories,
  MonitoringCounts,
  SubscriptionConfiguration,
} from '@/types';
import { cn } from '@/utils/classname';

import { NotIncludedBadge } from './NotIncludedBadge';

const CATEGORY_ATTACK_SURFACE_MAP: Record<string, string> = {
  externalNetwork: 'external',
  applications: 'applications',
  internalNetwork: 'internal',
  code: 'code',
};

const MONITORING_CATEGORY_DESC_MAP: Record<MonitoringCategories, string> = {
  cloud: 'Total Number of Cloud Assets Monitored',
  internalNetwork: 'Total Number of Internal Network Assets Monitored',
  externalNetwork: 'Total Number of Perimeter Network Assets Monitored',
  code: 'Total Number of Repositories Monitored',
  applications: 'Total Number of Perimeter Applications Monitored',
};

function calculateCategoryTotal(
  category: Record<string, number | Record<string, number>>
): number {
  return Object.values(category).reduce((acc, curr) => {
    const currValue =
      typeof curr === 'number'
        ? curr
        : Object.values(curr).reduce((acc, curr) => acc + curr, 0);

    return (acc as number) + currValue;
  }, 0) as number;
}

interface MonitoringCategoryCardProps {
  categoryKey: MonitoringCategories;
  categoryIcon: ReactNode;
  category: Record<string, number | Record<string, number>>;
  monitoringCounts: MonitoringCounts;
  subscriptionValue?: SubscriptionConfiguration;
  isLoading: boolean;
}

export function MonitoringCategoryCard({
  categoryKey,
  categoryIcon,
  category,
  monitoringCounts,
  subscriptionValue,
  isLoading,
}: MonitoringCategoryCardProps) {
  const categoryItems = Object.entries(category);
  const categoryItemsExist = categoryItems.length > 0;

  // For internalNetwork, filter out ADUser (moved to Social Engineering)
  const filteredEntries =
    categoryKey === 'internalNetwork'
      ? categoryItems.filter(([entryKey]) => entryKey !== 'ADUser')
      : categoryItems;

  // For internalNetwork, exclude ADUser from the displayed total
  const displayTotal =
    categoryKey === 'internalNetwork'
      ? (
          calculateCategoryTotal(category) -
          (monitoringCounts.internalNetwork.ADUser || 0)
        ).toString()
      : categoryKey === 'applications' && 'webseed' in category
        ? (category.webseed as number).toString()
        : calculateCategoryTotal(category).toString();

  const attackSurfaceKey = CATEGORY_ATTACK_SURFACE_MAP[categoryKey];
  const isCovered = attackSurfaceKey
    ? Boolean(
        subscriptionValue?.attackSurface?.[
          attackSurfaceKey as keyof typeof subscriptionValue.attackSurface
        ]
      )
    : true;

  return (
    <Container className="flex h-full w-full flex-col gap-3">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold">
            {MONITORING_NAME_MAP[categoryKey]}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {!isCovered && <NotIncludedBadge />}
          <Loader
            className="my-[5.5px] h-[16px] w-[48px] rounded-3xl"
            isLoading={isLoading}
          >
            <div className="border-chariot-stroke text-chariot-text-primary rounded-2xl border px-3 py-1 text-xs font-medium">
              now
            </div>
          </Loader>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {categoryIcon}
        <h4 className="text-sm font-bold whitespace-break-spaces">
          {MONITORING_CATEGORY_DESC_MAP[categoryKey]}
        </h4>
        <Loader
          className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
          isLoading={isLoading}
        >
          <div className="bg-chariot-background-secondary ml-auto min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
            {displayTotal}
          </div>
        </Loader>
      </div>
      {categoryItemsExist && (
        <>
          <div className={cn('bg-chariot-stroke h-px w-full')} />
          <div className="flex flex-col gap-4">
            {filteredEntries.map(([entryKey, value]) => {
              return (
                <div className="flex flex-col gap-2" key={entryKey}>
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold whitespace-break-spaces capitalize">
                        {MONITORING_NAME_MAP[entryKey] || entryKey}
                      </h4>
                      <Tooltip title={MONITORING_TOOLTIP_MAP[entryKey] || ''}>
                        <InfoIcon className="text-chariot-text-primary size-4" />
                      </Tooltip>
                    </div>
                    <Loader
                      className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
                      isLoading={isLoading}
                    >
                      <div className="bg-chariot-background-secondary min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
                        {typeof value === 'number'
                          ? value.toString()
                          : calculateCategoryTotal(value).toString()}
                      </div>
                    </Loader>
                  </div>
                  {typeof value === 'object' && (
                    <div className="flex flex-col gap-1">
                      {Object.entries(value)
                        .filter(([subKey]) => subKey !== 'resources')
                        .map(([subKey, subValue]) => {
                          return (
                            <div
                              key={subKey}
                              className="flex w-full items-center justify-between pl-4"
                            >
                              <div className="flex items-center gap-2">
                                <h4 className="text-chariot-text-primary text-sm whitespace-break-spaces capitalize">
                                  {MONITORING_NAME_MAP[subKey]}
                                </h4>
                              </div>
                              <Loader
                                className="my-[5.5px] ml-auto h-[16px] w-[48px] rounded-3xl"
                                isLoading={isLoading}
                              >
                                <div className="bg-chariot-background-secondary min-w-12 rounded-md px-3 py-1 text-center text-sm font-bold">
                                  {(subValue as number).toString()}
                                </div>
                              </Loader>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Container>
  );
}
